import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CircleMarker, GeoJSON as GeoJSONLayer, Tooltip, useMap } from 'react-leaflet';
import IgnisIcon from '../components/icons/IgnisIcon';
import CountryRiskLayer from '../components/map/CountryRiskLayer';
import MapShell from '../components/map/MapShell';
import { useAuth } from '../contexts/AuthProvider';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { useNowcastZones } from '../hooks/useNowcastZones';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { loadConcelhos, type Concelho } from '../lib/concelhos';

// Fire nowcast (premium tier over the briefing): ONE operational dashboard per
// municipality — a zone selector, then that concelho's 1 km map with every
// active ANEPC rural fire (inside + 20 km) as markers, and a compact fire
// list. Data baked to R2 by scripts/fire_watcher.py (fire-nowcast-plan.md
// Phase A). Access: briefing_subscriptions zones x feature_grants('nowcast');
// 'nowcast_admin' grants unlock every concelho (owner/ops view). The page
// self-gates like Briefing.
//
// The PROPAGATION layer (spread-sim footprints) stays experimental-only until
// the engine re-gate passes (docs/prod/SPREAD_ROS_PLAN.md): default-off
// per-fire toggle, hard disclaimer, and the watcher only bakes footprints
// when IGNIS_NOWCAST_FOOTPRINTS=1 (dev).

const CONTACT_EMAIL = 'institucional@tutela.land';
const MAILTO = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
  'Pedido de acesso ao Nowcast IGNIS',
)}`;

const REFRESH_MS = 120_000;

const DISCLAIMER =
  'Fonte das ocorrências: ANEPC/SADO via AGIF SGIFR (CC BY 4.0), atualizada ao minuto; os ' +
  'números de despacho da Proteção Civil incluem ocorrências que o ICNF não consolida. Esta ' +
  'página é apoio à decisão e não substitui o comando ANEPC nem os avisos oficiais.';

const FOOTPRINT_WARNING =
  'Projeção EXPERIMENTAL em validação: o motor de propagação está em desenvolvimento e ' +
  'as áreas projetadas NÃO são fiáveis (ver Validação). Nunca use esta camada para decisões ' +
  'operacionais.';

interface NowcastFire {
  numero: string;
  natureza: string | null;
  estado: string | null;
  estado_agrupado: string | null;
  concelho_anepc: string | null;
  dico: string | null;
  freguesia: string | null;
  lat: number;
  lon: number;
  started_at: string | null;
  first_seen: string | null;
  duracao_min: number | null;
  operacionais: number | null;
  meios_terrestres: number | null;
  meios_aereos: number | null;
  concelho_slug: string | null;
  zones_nearby: { slug: string; distance_km: number }[];
  footprint: string | null;
}

interface NowcastDoc {
  generated_at: string;
  source: string;
  trigger_radius_km: number;
  footprints_enabled: boolean;
  n_active: number;
  fires: NowcastFire[];
}

function fmtSince(iso: string | null): string {
  if (!iso) return '';
  const mins = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60000));
  if (mins < 60) return `${mins} min`;
  return `${Math.floor(mins / 60)} h ${mins % 60} min`;
}

function distanceTo(fire: NowcastFire, slug: string): number | null {
  const z = fire.zones_nearby.find((z) => z.slug === slug);
  return z ? z.distance_km : null;
}

function FitZone({
  bbox,
  fires,
}: {
  bbox: [number, number, number, number];
  fires: NowcastFire[];
}) {
  const map = useMap();
  useEffect(() => {
    // bbox is [minLon, minLat, maxLon, maxLat]; extend to include the fires
    let [minLon, minLat, maxLon, maxLat] = bbox;
    for (const f of fires) {
      minLon = Math.min(minLon, f.lon);
      minLat = Math.min(minLat, f.lat);
      maxLon = Math.max(maxLon, f.lon);
      maxLat = Math.max(maxLat, f.lat);
    }
    map.fitBounds(
      [
        [minLat, minLon],
        [maxLat, maxLon],
      ],
      { padding: [18, 18] },
    );
  }, [map, bbox, fires]);
  return null;
}

function ZoneMap({
  concelho,
  fires,
  footprintFire,
}: {
  concelho: Concelho;
  fires: NowcastFire[];
  footprintFire: NowcastFire | null;
}) {
  const [cells, setCells] = useState<number[][] | null>(null);
  const [footprint, setFootprint] = useState<GeoJSON.GeoJsonObject | null>(null);
  const cdn = import.meta.env.VITE_DATA_URL;

  useEffect(() => {
    if (!cdn) return;
    let cancelled = false;
    setCells(null);
    fetch(`${cdn}/concelho_cells/${concelho.slug}.json`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((doc: { cells: number[][] }) => !cancelled && setCells(doc.cells))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [cdn, concelho.slug]);

  useEffect(() => {
    if (!cdn || !footprintFire?.footprint) {
      setFootprint(null);
      return;
    }
    let cancelled = false;
    fetch(`${cdn}/${footprintFire.footprint}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((doc: { burned_geojson: GeoJSON.GeoJsonObject }) =>
        !cancelled && setFootprint(doc.burned_geojson),
      )
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [cdn, footprintFire]);

  return (
    <div className="mt-4 h-96 overflow-hidden rounded-lg border border-ink-700">
      <MapShell
        tileUrl="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
        tileAttribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
        zoomControl={false}
        className="h-full w-full"
      >
        <FitZone bbox={concelho.bbox} fires={fires} />
        {cells && <CountryRiskLayer cells={cells} viewMode="risk" />}
        {footprint && (
          <GeoJSONLayer
            key={footprintFire?.footprint ?? 'fp'}
            data={footprint}
            style={{ color: '#F26A4B', weight: 0.5, fillOpacity: 0.25 }}
          />
        )}
        {fires.map((f) => (
          <CircleMarker
            key={f.numero}
            center={[f.lat, f.lon]}
            radius={f.estado_agrupado === 'Em Curso' || f.estado_agrupado === 'Em Despacho' ? 9 : 7}
            pathOptions={{
              color: '#F26A4B',
              fillColor: '#F26A4B',
              fillOpacity:
                f.estado_agrupado === 'Em Curso' || f.estado_agrupado === 'Em Despacho'
                  ? 0.9
                  : 0.45,
              weight: 2,
            }}
          >
            <Tooltip direction="top" offset={[0, -8]}>
              {f.concelho_anepc} · {f.natureza ?? 'Ocorrência'} ·{' '}
              {f.estado_agrupado ?? f.estado ?? ''}
            </Tooltip>
          </CircleMarker>
        ))}
      </MapShell>
    </div>
  );
}

function FireRow({
  fire,
  zoneSlug,
  footprintsEnabled,
  footprintShown,
  onToggleFootprint,
}: {
  fire: NowcastFire;
  zoneSlug: string;
  footprintsEnabled: boolean;
  footprintShown: boolean;
  onToggleFootprint: (numero: string) => void;
}) {
  const dist = distanceTo(fire, zoneSlug);
  const inside = fire.concelho_slug === zoneSlug;
  const active =
    fire.estado_agrupado === 'Em Curso' || fire.estado_agrupado === 'Em Despacho';
  const canFootprint = footprintsEnabled && fire.footprint != null;

  return (
    <div className="rounded-lg border border-ink-700 bg-ink-950/60 px-4 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="font-display font-bold text-mist">
          {fire.concelho_anepc ?? '—'}
          {fire.freguesia && (
            <span className="ml-2 text-sm font-normal text-mist-muted">{fire.freguesia}</span>
          )}
        </div>
        <span
          className={`rounded-full px-2.5 py-0.5 font-mono text-xs font-semibold ${
            active ? 'bg-ember/20 text-ember' : 'bg-ink-800 text-mist-muted'
          }`}
        >
          {fire.estado_agrupado ?? fire.estado ?? 'ativo'}
        </span>
      </div>
      <div className="mt-1 font-mono text-xs text-mist-faint">
        {fire.natureza ?? 'fogo rural'} · nº {fire.numero}
        {fire.started_at && <> · início há {fmtSince(fire.started_at)}</>}
        {' · '}
        {inside ? (
          <span className="font-semibold text-ember">dentro do concelho</span>
        ) : (
          dist != null && <>a {dist} km do concelho</>
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-mist-muted">
        <span>
          Operacionais: <span className="font-semibold text-mist">{fire.operacionais ?? '—'}</span>
        </span>
        <span>
          Terrestres:{' '}
          <span className="font-semibold text-mist">{fire.meios_terrestres ?? '—'}</span>
        </span>
        <span>
          Aéreos: <span className="font-semibold text-mist">{fire.meios_aereos ?? '—'}</span>
        </span>
        {canFootprint && (
          <label className="flex cursor-pointer items-center gap-1.5 text-xs text-mist-muted">
            <input
              type="checkbox"
              checked={footprintShown}
              onChange={() => onToggleFootprint(fire.numero)}
              className="accent-ember"
            />
            projeção experimental
          </label>
        )}
      </div>
    </div>
  );
}

function ZoneDashboard({
  concelho,
  fires,
  footprintsEnabled,
}: {
  concelho: Concelho;
  fires: NowcastFire[];
  footprintsEnabled: boolean;
}) {
  const [footprintNumero, setFootprintNumero] = useState<string | null>(null);
  const inside = fires.filter((f) => f.concelho_slug === concelho.slug);
  const nearby = fires.filter((f) => f.concelho_slug !== concelho.slug);
  const footprintFire = fires.find((f) => f.numero === footprintNumero) ?? null;

  return (
    <div className="rounded-xl border border-ink-700 bg-ink-900 p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-display text-2xl font-bold text-mist">{concelho.nome}</h2>
        <span className="font-mono text-xs text-mist-faint">
          distrito de {concelho.distrito} · {inside.length} no concelho · {nearby.length} até 20
          km
        </span>
      </div>

      {fires.length === 0 ? (
        <p className="mt-4 rounded-lg border border-ink-700 bg-ink-950/60 px-4 py-6 text-center text-sm text-mist-muted">
          Sem ocorrências rurais ativas no concelho ou a menos de 20 km.
        </p>
      ) : (
        <>
          <ZoneMap concelho={concelho} fires={fires} footprintFire={footprintFire} />
          {footprintFire && (
            <p className="mt-3 rounded-lg border border-amber-500/50 bg-amber-500/10 px-4 py-3 text-xs leading-relaxed text-mist">
              <span className="font-semibold">Aviso:</span> {FOOTPRINT_WARNING}
            </p>
          )}
          <div className="mt-4 space-y-3">
            {[...inside, ...nearby].map((f) => (
              <FireRow
                key={f.numero}
                fire={f}
                zoneSlug={concelho.slug}
                footprintsEnabled={footprintsEnabled}
                footprintShown={footprintNumero === f.numero}
                onToggleFootprint={(n) =>
                  setFootprintNumero((cur) => (cur === n ? null : n))
                }
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function RestrictedExplainer() {
  const { user } = useAuth();
  return (
    <div className="min-h-[calc(100vh-4rem)] bg-ink-950 text-mist">
      <div className="mx-auto max-w-3xl px-6 py-20">
        <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-ember/30 bg-ember/10 px-4 py-1.5 font-mono text-sm font-semibold text-ember">
          <IgnisIcon className="h-4 w-4" />
          IGNIS · Nowcast
        </div>

        <h1 className="font-display text-3xl font-bold tracking-tight text-mist sm:text-4xl">
          Fogos ativos nas suas zonas, ao minuto
        </h1>

        <p className="mt-6 text-lg leading-relaxed text-mist-muted">
          Monitorização contínua das ocorrências rurais ANEPC no seu concelho e num raio de
          20 km: estado, meios empenhados, localização no mapa de risco de 1 km. Concebido
          para serviços municipais de proteção civil e parceiros autorizados.
        </p>

        <div className="mt-10 rounded-xl border border-ember/40 bg-ember/10 px-5 py-4">
          {user ? (
            <p className="text-sm leading-relaxed text-mist">
              A sua conta (<span className="font-mono text-ember">{user.email}</span>) ainda não
              tem acesso ao nowcast. Solicite acesso abaixo.
            </p>
          ) : (
            <p className="text-sm leading-relaxed text-mist">
              O acesso ao nowcast requer uma conta autorizada.{' '}
              <Link to="/entrar" className="font-semibold text-ember hover:underline">
                Entrar
              </Link>{' '}
              ou solicite acesso abaixo.
            </p>
          )}
        </div>

        <div className="mt-10 rounded-xl border border-ink-700 bg-ink-900 p-6">
          <h2 className="font-display text-xl font-bold text-mist">Como pedir acesso</h2>
          <p className="mt-3 leading-relaxed text-mist-muted">
            O acesso é reservado a municípios (todo o domínio institucional, p. ex.{' '}
            <span className="font-mono text-sm">cm-exemplo.pt</span>) e a endereços de
            parceiros autorizados, limitado aos concelhos contratados. Contacte-nos a partir
            de um endereço institucional, indicando a entidade e os concelhos pretendidos.
          </p>
          <a
            href={MAILTO}
            className="mt-5 inline-flex items-center gap-2.5 rounded-lg bg-ember px-6 py-3 font-semibold text-ink-950 shadow-[0_0_30px_-8px_#F26A4B] transition-all duration-200 hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ember"
          >
            Solicitar acesso
          </a>
          <p className="mt-4 font-mono text-sm text-mist-faint">{CONTACT_EMAIL}</p>
        </div>

        <p className="mt-10 text-sm text-mist-muted">
          Subscritor do briefing? O{' '}
          <Link to="/ignis/briefing" className="font-medium text-ember hover:brightness-110">
            briefing diário
          </Link>{' '}
          continua disponível com a sua conta.
        </p>
      </div>
    </div>
  );
}

function NowcastView({ zones, isAdmin }: { zones: string[]; isAdmin: boolean }) {
  const [doc, setDoc] = useState<NowcastDoc | null>(null);
  const [concelhos, setConcelhos] = useState<Concelho[] | null>(null);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState<string | null>(zones[0] ?? null);
  const [adminQuery, setAdminQuery] = useState('');

  const refresh = useCallback(() => {
    const cdn = import.meta.env.VITE_DATA_URL;
    if (!cdn) {
      setError(true);
      return;
    }
    fetch(`${cdn}/nowcast/active.json`, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d: NowcastDoc) => {
        setDoc(d);
        setError(false);
      })
      .catch(() => setError(true));
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadConcelhos()
      .then((staticDoc) => !cancelled && setConcelhos(staticDoc.concelhos))
      .catch(() => !cancelled && setError(true));
    refresh();
    const timer = setInterval(refresh, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [refresh]);

  const bySlugStatic = useMemo(
    () => new Map((concelhos ?? []).map((c) => [c.slug, c])),
    [concelhos],
  );

  if (error && !doc) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-20 text-mist-muted">
        Não foi possível carregar as ocorrências. Tente novamente dentro de momentos.
      </div>
    );
  }
  if (!doc || !concelhos) {
    return (
      <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center bg-ink-950">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-ink-700 border-t-ember" />
      </div>
    );
  }

  const firesFor = (slug: string) =>
    doc.fires.filter((f) => f.zones_nearby.some((z) => z.slug === slug));

  // Admin extra: any concelho with active fires nearby is one search away.
  const adminMatches =
    isAdmin && adminQuery.trim().length >= 2
      ? concelhos
          .filter((c) => c.nome.toLowerCase().includes(adminQuery.trim().toLowerCase()))
          .slice(0, 8)
      : [];

  const currentSlug = selected ?? zones[0] ?? null;
  const current = currentSlug ? bySlugStatic.get(currentSlug) : undefined;
  const zonePill = (slug: string) => {
    const n = firesFor(slug).length;
    const activeSel = currentSlug === slug;
    return (
      <button
        key={slug}
        onClick={() => setSelected(slug)}
        className={`rounded-full px-3.5 py-1.5 font-mono text-xs transition-colors ${
          activeSel ? 'bg-ember text-ink-950 font-semibold' : 'bg-ink-800 text-mist-muted hover:text-mist'
        }`}
      >
        {bySlugStatic.get(slug)?.nome ?? slug}
        {n > 0 && (
          <span className={activeSel ? '' : 'text-ember'}>
            {' '}
            · {n}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-ink-950 text-mist">
      <div className="mx-auto max-w-3xl px-6 py-14">
        <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-ember/30 bg-ember/10 px-4 py-1.5 font-mono text-sm font-semibold text-ember">
          <IgnisIcon className="h-4 w-4" />
          IGNIS · Nowcast{isAdmin && ' · admin'}
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Fogos ativos</h1>
        <p className="mt-2 font-mono text-sm text-mist-faint">
          atualizado {fmtSince(doc.generated_at)} atrás ·{' '}
          {doc.n_active === 1
            ? '1 ocorrência rural no continente'
            : `${doc.n_active} ocorrências rurais no continente`}{' '}
          · raio {doc.trigger_radius_km} km
        </p>

        {(zones.length > 1 || isAdmin) && (
          <div className="mt-6 flex flex-wrap items-center gap-2">
            {zones.map(zonePill)}
            {isAdmin && (
              <div className="relative">
                <input
                  value={adminQuery}
                  onChange={(e) => setAdminQuery(e.target.value)}
                  placeholder="qualquer concelho…"
                  className="w-44 rounded-full border border-ink-700 bg-ink-900 px-3.5 py-1.5 font-mono text-xs text-mist placeholder:text-mist-faint focus:border-ember focus:outline-none"
                />
                {adminMatches.length > 0 && (
                  <div className="absolute z-[1000] mt-1 w-56 overflow-hidden rounded-lg border border-ink-700 bg-ink-900 shadow-xl">
                    {adminMatches.map((c) => (
                      <button
                        key={c.slug}
                        onClick={() => {
                          setSelected(c.slug);
                          setAdminQuery('');
                        }}
                        className="block w-full px-3.5 py-2 text-left font-mono text-xs text-mist-muted hover:bg-ink-800 hover:text-mist"
                      >
                        {c.nome}{' '}
                        <span className="text-mist-faint">({c.distrito})</span>
                        {firesFor(c.slug).length > 0 && (
                          <span className="text-ember"> · {firesFor(c.slug).length}</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="mt-6">
          {current ? (
            <ZoneDashboard
              concelho={current}
              fires={firesFor(current.slug)}
              footprintsEnabled={doc.footprints_enabled}
            />
          ) : (
            <div className="rounded-xl border border-ink-700 bg-ink-900 p-8 text-center text-mist-muted">
              {isAdmin
                ? 'Pesquise um concelho para abrir o painel.'
                : 'Sem zonas atribuídas.'}
            </div>
          )}
        </div>

        <p className="mt-10 text-xs leading-relaxed text-mist-faint">{DISCLAIMER}</p>
      </div>
    </div>
  );
}

export default function Nowcast() {
  useDocumentMeta(
    'Nowcast de fogos ativos (acesso restrito) | Tutela IGNIS',
    'Monitorização ao minuto de ocorrências rurais ANEPC no seu concelho e num raio de 20 km. Acesso reservado a municípios e parceiros autorizados.',
    undefined, // gated: no canonical, noindex
    { noindex: true },
  );

  const { zones, loading: zonesLoading } = useNowcastZones();
  const { access: adminAccess, loading: adminLoading } = useFeatureAccess('nowcast_admin');
  const isAdmin = adminAccess === true;

  if (zonesLoading || adminLoading) {
    return (
      <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center bg-ink-950">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-ink-700 border-t-ember" />
      </div>
    );
  }

  return (zones && zones.length > 0) || isAdmin ? (
    <NowcastView zones={zones ?? []} isAdmin={isAdmin} />
  ) : (
    <RestrictedExplainer />
  );
}
